import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { LockedIcon, WorkspaceIcon } from '@sketchnote/icons';
import { Button, Radio } from '@sketchnote/shared-components';
import { Emoji } from 'emoji-mart';
import update from 'immutability-helper';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { API } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { useSidebarStore } from 'src/components/sidebar/store';
import { PLANRULES } from 'src/constants/plans';
import { EmojiPopover, handleUpdateFieldsProps } from 'src/features/EmojiPopover';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getAllOrgMembers } from 'src/utils/getMemberDetails';

import { SelectedOption, ShareMultiSelect } from '../share';
import { useWorkspace } from '../store/useWorkspace';
export const CreateWorkspace = ({
  onClose,
  totalWorkspaces,
  hidePrivacy = false,
  isOnBording = false,
  workspace,
  learnMode = false,
}: {
  onClose: () => void;
  totalWorkspaces: number;
  hidePrivacy?: boolean;
  isOnBording?: boolean;
  workspace?: { name: string; icon: any };
  learnMode?: boolean;
}) => {
  const [createWorkspace, setCreateWorkspace] = useState({
    privacy: 'public',
    workspaceName: '',
    icon: WorkspaceIcon,
  });

  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const [workspaceIcon, setWorkspaceIcon] = useState<any>();
  const { onSearchUsers, onCreateWorkspace } = useWorkspace();
  const handleRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateWorkspace(update(createWorkspace, { $merge: { privacy: event.target.value } }));
  };
  const handleSelectedOption = (value) => {
    setSelectedOptions(value);
  };
  const [descrptionText, setDescrptionText] = useState('');
  const fetchSidebar = useSidebarStore((s) => s.fetchSidebar);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const authData = useAuth((s) => s.userDetails);
  const router = useRouter();
  const [openSnackbar] = useSnackbar();

  const handleCreateWorkspace = async () => {
    if (createWorkspace.workspaceName.trim().length !== 0) {
      const member = selectedOptions.map((user) => user.memberId).filter((x) => x);
      const email = selectedOptions
        .map((user) => (user.memberId ? '' : user.name))
        .filter((x) => x);

      const response = await onCreateWorkspace({
        name: createWorkspace.workspaceName,
        privacy: createWorkspace.privacy,
        members: member,
        emails: email,
        icon: workspaceIcon?.icon,
        description: descrptionText,
      });
      //organisationMembers.length - 1 to exclude owner of workspace
      if (response) {
        fetchSidebar(router?.query?.mode as string);
        Analytics.track('Workspace created', {
          'Workspace name': createWorkspace.workspaceName,
          'Workspace privacy': createWorkspace.privacy,
          'No. of members in workspace':
            createWorkspace.privacy === 'private' ? member.length : getAllOrgMembers()?.length - 1,
        });
        onClose();
      }
    } else {
      openSnackbar({
        text: 'Workspace name cannot be blank',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };

  const toggleEmojiPicker = (value?: boolean) => () => {
    setIsEmojiPickerOpen(value ?? !isEmojiPickerOpen);
  };

  const handleUpdateFields = ({ icon }: handleUpdateFieldsProps) => {
    setWorkspaceIcon({ icon: { id: icon?.id, type: icon?.type } });
    setIsEmojiPickerOpen(false);
  };

  const isUpgradeRequired = useMemo(() => {
    return authData?.plan.type ? totalWorkspaces >= PLANRULES.workspace[authData.plan.type] : false;
  }, []);

  return (
    <div className="bg-white rounded-lg">
      {learnMode ? (
        <div className="flex-col mb-26">
          <p className="text-2xl text-gray-999 font-medium mb-8">Create a course collection</p>
          <p className="text-xs font-regular text-gray-600">
            Make your own module or lesson using Sketchnote’s illustration library and your custom
            content
          </p>
        </div>
      ) : (
        <p className="text-2xl text-gray-999 font-medium mb-32">New Workspace</p>
      )}
      <div className="flex">
        <div>
          <label htmlFor="input" className="text-gray-600 text-sm mb-8 block">
            Icon
          </label>
          <Popover
            offset={[160, 10]}
            isOpen={isEmojiPickerOpen}
            onClose={toggleEmojiPicker(false)}
            onOpen={toggleEmojiPicker(true)}
            modifiers={[{ name: 'eventListeners', options: { scroll: isEmojiPickerOpen } }]}
          >
            <PopoverTrigger>
              <button
                disabled={isOnBording}
                className="border border-gray-100 shadow-md bg-gray-199 rounded-sm mr-12 flex items-center justify-center h-40 w-40"
              >
                {/* for onboarindg flow icons selected by user will be shown */}
                {isOnBording ? (
                  workspace?.icon.type === 'image' ? (
                    <img
                      className="w-18 h-18 object-center object-cover"
                      src={workspace?.icon?.id}
                    />
                  ) : (
                    <Emoji
                      emoji={
                        workspace?.icon?.id || {
                          id: 'file_folder',
                        }
                      }
                      set="apple"
                      size={18}
                    />
                  )
                ) : workspaceIcon?.icon?.type === 'image' ? (
                  <img
                    className="w-18 h-18 object-center object-cover"
                    src={workspaceIcon?.icon?.id}
                  />
                ) : (
                  <Emoji
                    emoji={
                      workspaceIcon?.icon?.id || {
                        id: 'file_folder',
                      }
                    }
                    set="apple"
                    size={18}
                  />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent>
              {isEmojiPickerOpen && <EmojiPopover onChangeIcon={handleUpdateFields} />}
            </PopoverContent>
          </Popover>
        </div>
        <div className="ml-12 w-full">
          <label htmlFor="input" className="text-gray-600 text-sm mb-8 block">
            {learnMode ? 'Lesson name' : 'Name'}
          </label>
          <input
            disabled={isOnBording}
            value={isOnBording ? workspace?.name : createWorkspace.workspaceName}
            className="p-12 bg-gray-100 mb-32 w-full border rounded"
            placeholder={
              isOnBording ? (learnMode ? 'My first lesson' : 'My first workspace') : 'My workspace'
            }
            onChange={(e) => {
              setCreateWorkspace(
                update(createWorkspace, { $merge: { workspaceName: e.target.value } })
              );
            }}
          />
        </div>
      </div>
      <div className="w-full mb-32">
        <label htmlFor="input" className="text-gray-600 text-sm mb-8 block">
          Description
        </label>
        <textarea
          disabled={isOnBording}
          className="bg-gray-100 w-full h-90 border rounded-sm p-12 border-gray-200"
          onChange={(e) => setDescrptionText(e.target.value)}
          rows={2}
          value={descrptionText}
          placeholder={
            isOnBording
              ? learnMode
                ? 'Write about this course collection briefly'
                : 'Write about this workspace briefly'
              : 'Click to add workspace description'
          }
          style={{ height: '90px', resize: 'none' }}
        />
      </div>
      {!hidePrivacy && (
        <>
          <label htmlFor="privacy" className="text-sm text-black mb-16 block">
            Privacy
          </label>

          <Radio
            name="privacy"
            value={'public'}
            label={'Public for all team members'}
            checked={createWorkspace.privacy === 'public'}
            onChange={handleRadioButtonChange}
            className="text-sm mb-20"
            innerClassName="mb-20"
          />

          <Radio
            name="privacy"
            value={'private'}
            label={'Private (only for team members with invite)'}
            checked={createWorkspace.privacy === 'private'}
            onChange={handleRadioButtonChange}
            className="text-sm"
          />

          {/* {createWorkspace.privacy === 'private' && (
            <div className="relative">
              <label htmlFor="members" className="text-sm text-gray-600 mt-32 mb-8 block">
                Members
              </label>
              <ShareMultiSelect
                wrapperClassNames="pl-0"
                resultOptions={onSearchUsers}
                selectedOptions={selectedOptions}
                onChangeSelectedValue={handleSelectedOption}
              />
            </div>
          )} */}
        </>
      )}
      <div className="flex mt-32 justify-end">
        {isOnBording && !learnMode && (
          <Button
            disabled={isOnBording}
            onClick={onClose}
            status={'secondary'}
            size="lg"
            className="justify-center mr-8"
          >
            Cancel
          </Button>
        )}
        <Button
          disabled={createWorkspace.workspaceName === '' || isUpgradeRequired}
          status={'primary'}
          size="lg"
          className="justify-center"
          onClick={handleCreateWorkspace}
        >
          {learnMode ? 'Create' : 'Create Workspace'}
          {isUpgradeRequired && <LockedIcon className="ml-6" />}
        </Button>
      </div>
      {isUpgradeRequired && (
        <div className="w-full p-6 bg-red-400 rounded-sm text-white text-xs mt-12 text-center">
          Please upgrade to continue creating workspaces
        </div>
      )}
    </div>
  );
};
